import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Camera, Crop, Check, RotateCcw, Loader2, MonitorUp, ScanSearch,
  TabletSmartphone, ImagePlus, ShieldAlert
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * Screen capture dialog using the native Screen Capture API (getDisplayMedia).
 * The browser's native picker lets the user choose: entire screen, an
 * application window, or a browser tab. The user can then freeze a frame and
 * either use it entirely or crop a selected region.
 */
export const ScreenCaptureDialog = ({ open, onOpenChange, onCapture, allowAnalyzeNow = false, t }) => {
  const [phase, setPhase] = useState('starting'); // starting | live | frozen | unsupported | denied
  const [cropMode, setCropMode] = useState(false);
  const [cropRect, setCropRect] = useState(null); // {x, y, w, h} as fractions 0..1
  const [frozenUrl, setFrozenUrl] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const frameCanvasRef = useRef(null);
  const imgWrapRef = useRef(null);
  const dragRef = useRef(null);
  const fileInputRef = useRef(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startCapture = useCallback(async () => {
    setPhase('starting');
    setFrozenUrl(null);
    setCropRect(null);
    setCropMode(false);
    // iPad/iPhone browsers (WebKit) do not implement getDisplayMedia at all:
    // there is NO permission the user could grant. Offer fallbacks instead.
    if (!navigator.mediaDevices?.getDisplayMedia) {
      setPhase('unsupported');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: false,
      });
      streamRef.current = stream;
      setPhase('live');
      // Attach stream once video element is mounted
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      });
    } catch (e) {
      // NotAllowedError: the user dismissed/denied the browser picker,
      // or a permissions policy blocks display-capture.
      setPhase('denied');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('capture.error'));
      return;
    }
    onCapture(file, false);
    onOpenChange(false);
    toast.success(t('capture.success'));
  };

  useEffect(() => {
    if (open) {
      startCapture();
    } else {
      stopStream();
      setFrozenUrl(null);
      setCropRect(null);
      setCropMode(false);
    }
    return stopStream;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const snapFrame = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      toast.error(t('capture.error'));
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    frameCanvasRef.current = canvas;
    setFrozenUrl(canvas.toDataURL('image/png'));
    setPhase('frozen');
    stopStream();
  };

  const makeFileAndFinish = (canvas, analyzeNow = false) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error(t('capture.error'));
        return;
      }
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const file = new File([blob], `capture_${stamp}.png`, { type: 'image/png' });
      onCapture(file, analyzeNow);
      onOpenChange(false);
      toast.success(t('capture.success'));
    }, 'image/png');
  };

  const captureFullFrame = (analyzeNow = false) => {
    if (frameCanvasRef.current) makeFileAndFinish(frameCanvasRef.current, analyzeNow);
  };

  const validateCrop = (analyzeNow = false) => {
    const src = frameCanvasRef.current;
    if (!src || !cropRect || cropRect.w < 0.01 || cropRect.h < 0.01) return;
    const sx = Math.round(cropRect.x * src.width);
    const sy = Math.round(cropRect.y * src.height);
    const sw = Math.max(1, Math.round(cropRect.w * src.width));
    const sh = Math.max(1, Math.round(cropRect.h * src.height));
    const out = document.createElement('canvas');
    out.width = sw;
    out.height = sh;
    out.getContext('2d').drawImage(src, sx, sy, sw, sh, 0, 0, sw, sh);
    makeFileAndFinish(out, analyzeNow);
  };

  // ===== Crop drag handling =====
  const getPercent = (e) => {
    const rect = imgWrapRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return null;
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  };

  const handleCropMouseDown = (e) => {
    if (!cropMode || e.button !== 0) return;
    const p = getPercent(e);
    if (!p) return;
    dragRef.current = p;
    setCropRect({ x: p.x, y: p.y, w: 0, h: 0 });
    e.preventDefault();
  };

  useEffect(() => {
    if (!cropMode) return;
    const onMove = (e) => {
      if (!dragRef.current) return;
      const p = getPercent(e);
      if (!p) return;
      const start = dragRef.current;
      setCropRect({
        x: Math.min(start.x, p.x),
        y: Math.min(start.y, p.y),
        w: Math.abs(p.x - start.x),
        h: Math.abs(p.y - start.y),
      });
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [cropMode]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(210,18%,9%)] border-border sm:max-w-4xl" data-testid="screen-capture-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <MonitorUp size={17} className="text-cyan-400" />
            {t('capture.title')}
          </DialogTitle>
        </DialogHeader>

        {/* STARTING */}
        {phase === 'starting' && (
          <div className="flex flex-col items-center justify-center py-14 gap-3">
            <Loader2 size={28} className="text-cyan-400 animate-spin" />
            <p className="text-xs text-muted-foreground text-center max-w-sm">{t('capture.picker_hint')}</p>
          </div>
        )}

        {/* UNSUPPORTED DEVICE (iPad/iPhone - WebKit has no getDisplayMedia) */}
        {phase === 'unsupported' && (
          <div className="flex flex-col items-center py-6 gap-4" data-testid="capture-unsupported-panel">
            <div className="w-12 h-12 rounded-full bg-cyan-500/15 ring-1 ring-cyan-500/30 flex items-center justify-center">
              <TabletSmartphone size={22} className="text-cyan-300" />
            </div>
            <div className="text-center space-y-1.5 max-w-md">
              <p className="text-sm font-semibold text-foreground">{t('capture.unsupported_title')}</p>
              <p className="text-xs text-muted-foreground">{t('capture.unsupported_desc')}</p>
            </div>
            <ol className="text-xs text-foreground/80 space-y-2 max-w-md w-full">
              <li className="flex items-start gap-2.5 p-2.5 rounded-lg bg-[hsl(210,14%,12%)] border border-border">
                <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-300 text-[10px] font-bold flex items-center justify-center">1</span>
                {t('capture.unsupported_step1')}
              </li>
              <li className="flex items-start gap-2.5 p-2.5 rounded-lg bg-[hsl(210,14%,12%)] border border-border">
                <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-300 text-[10px] font-bold flex items-center justify-center">2</span>
                {t('capture.unsupported_step2')}
              </li>
              <li className="flex items-start gap-2.5 p-2.5 rounded-lg bg-[hsl(210,14%,12%)] border border-border">
                <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-300 text-[10px] font-bold flex items-center justify-center">3</span>
                {t('capture.unsupported_step3')}
              </li>
            </ol>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImportFile}
              data-testid="capture-import-input"
            />
            <div className="flex flex-wrap justify-center gap-2">
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="gap-2 bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)]"
                data-testid="capture-import-button"
              >
                <ImagePlus size={15} />
                {t('capture.import_image')}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="text-xs" data-testid="capture-unsupported-cancel">
                {t('annotations.cancel')}
              </Button>
            </div>
          </div>
        )}

        {/* PERMISSION DENIED (desktop: user dismissed/denied the browser picker) */}
        {phase === 'denied' && (
          <div className="flex flex-col items-center py-8 gap-4" data-testid="capture-denied-panel">
            <div className="w-12 h-12 rounded-full bg-amber-500/15 ring-1 ring-amber-500/30 flex items-center justify-center">
              <ShieldAlert size={22} className="text-amber-300" />
            </div>
            <div className="text-center space-y-1.5 max-w-md">
              <p className="text-sm font-semibold text-foreground">{t('capture.denied_title')}</p>
              <p className="text-xs text-muted-foreground">{t('capture.denied_desc')}</p>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              <Button
                onClick={startCapture}
                className="gap-2 bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)]"
                data-testid="capture-retry-button"
              >
                <RotateCcw size={15} />
                {t('capture.retry')}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="text-xs" data-testid="capture-denied-cancel">
                {t('annotations.cancel')}
              </Button>
            </div>
          </div>
        )}

        {/* LIVE PREVIEW */}
        {phase === 'live' && (
          <div className="space-y-3">
            <div className="bg-black rounded-lg overflow-hidden flex items-center justify-center">
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="max-h-[55vh] max-w-full"
                data-testid="capture-live-video"
              />
            </div>
            <p className="text-[11px] text-muted-foreground text-center">{t('capture.live_hint')}</p>
            <div className="flex justify-center gap-2">
              <Button
                onClick={snapFrame}
                className="gap-2 bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)]"
                data-testid="capture-snap-button"
              >
                <Camera size={15} />
                {t('capture.snap')}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="text-xs" data-testid="capture-cancel-button">
                {t('annotations.cancel')}
              </Button>
            </div>
          </div>
        )}

        {/* FROZEN FRAME (+ optional crop) */}
        {phase === 'frozen' && frozenUrl && (
          <div className="space-y-3">
            <div className="bg-black rounded-lg overflow-hidden flex items-center justify-center">
              <div
                ref={imgWrapRef}
                className={`relative inline-block overflow-hidden ${cropMode ? 'cursor-crosshair' : ''}`}
                onMouseDown={handleCropMouseDown}
              >
                <img
                  src={frozenUrl}
                  alt="Captured frame"
                  className="max-h-[55vh] max-w-full block select-none"
                  draggable={false}
                  data-testid="capture-frozen-image"
                />
                {cropMode && cropRect && cropRect.w > 0 && cropRect.h > 0 && (
                  <div
                    className="absolute border-2 border-cyan-300 pointer-events-none"
                    style={{
                      left: `${cropRect.x * 100}%`,
                      top: `${cropRect.y * 100}%`,
                      width: `${cropRect.w * 100}%`,
                      height: `${cropRect.h * 100}%`,
                      boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
                    }}
                    data-testid="capture-crop-rect"
                  />
                )}
              </div>
            </div>

            {cropMode ? (
              <>
                <p className="text-[11px] text-cyan-300 text-center">{t('capture.crop_hint')}</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {allowAnalyzeNow && (
                    <Button
                      onClick={() => validateCrop(true)}
                      disabled={!cropRect || cropRect.w < 0.01 || cropRect.h < 0.01}
                      className="gap-2 bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)]"
                      data-testid="capture-crop-analyze-button"
                    >
                      <ScanSearch size={15} />
                      {t('capture.validate_and_analyze')}
                    </Button>
                  )}
                  <Button
                    onClick={() => validateCrop(false)}
                    disabled={!cropRect || cropRect.w < 0.01 || cropRect.h < 0.01}
                    variant={allowAnalyzeNow ? 'outline' : 'default'}
                    className={`gap-2 ${allowAnalyzeNow ? 'text-xs border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10' : 'bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)]'}`}
                    data-testid="capture-validate-crop-button"
                  >
                    <Check size={15} />
                    {t('capture.validate_area')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCropMode(false);
                      setCropRect(null);
                    }}
                    className="text-xs"
                    data-testid="capture-crop-cancel-button"
                  >
                    {t('annotations.cancel')}
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex flex-wrap justify-center gap-2">
                {allowAnalyzeNow && (
                  <Button
                    onClick={() => captureFullFrame(true)}
                    className="gap-2 bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)]"
                    data-testid="capture-analyze-now-button"
                  >
                    <ScanSearch size={15} />
                    {t('capture.analyze_now')}
                  </Button>
                )}
                <Button
                  onClick={() => captureFullFrame(false)}
                  variant={allowAnalyzeNow ? 'outline' : 'default'}
                  className={`gap-2 ${allowAnalyzeNow ? 'text-xs border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10' : 'bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)]'}`}
                  data-testid="capture-use-full-button"
                >
                  <Check size={15} />
                  {t('capture.use_full')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setCropMode(true)}
                  className="gap-2 text-xs border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10"
                  data-testid="capture-select-area-button"
                >
                  <Crop size={14} />
                  {t('capture.select_area')}
                </Button>
                <Button
                  variant="outline"
                  onClick={startCapture}
                  className="gap-2 text-xs"
                  data-testid="capture-retake-button"
                >
                  <RotateCcw size={14} />
                  {t('capture.retake')}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScreenCaptureDialog;
